import { messagingMediumText } from "../i18n/channel-view.js";
import { validatedDisplayView, extendDisplayView } from "../i18n/display-view.js";
import { portalFormat, portalLocale, portalText } from "../i18n/portal.js";
/**
 * Turning the P11-04/05/06 wire shapes into what Agent · Channels, Numbers & SIP and
 * Messaging render.
 *
 * It sits beside `api-gateway.ts` for the same reason `conversation-view.ts` does: both the
 * real gateway and the suites that check the decoding read it, and the console's copy for a
 * connection lives here — what "not observed" reads as, what a masked password looks like,
 * how a weekday is spelled. Copy, never configuration.
 *
 * **No field is invented and no absence is papered over.** Three absences in particular are
 * carried through rather than resolved:
 *
 *   `registration: null`   nothing has been observed. Not "not registered" — a carrier that
 *                          exposes no REGISTER leaves a gap, and a green badge over a gap is
 *                          a fabricated fact.
 *   `measured: false`      no WeTalk probe has reported. "region · latency" is not a vendor
 *                          field; there is nothing to fall back to.
 *   `channelUnitCeiling`   `null` when nothing is published. Not zero.
 */
import { money, type CurrencyCode } from "@wetalk/domain";
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readWholeNumber,
} from "./decode.js";
import { formatWhen, readMedium, readNullableText } from "./conversation-view.js";
import { NUMBER_RELEASE_CAUSE } from "./gateway.js";
import type {
  VoiceAgentConnectionView,
  AnsweringBehaviourView,
  AnsweringScheduleView,
  ChannelBindingRowView,
  ClosedDateView,
  ConcurrencySummaryView,
  InboundFailoverView,
  MediumState,
  MessagingMediumDetailView,
  MessagingMediumRowView,
  NumberActivityConversationView,
  NumberActivityView,
  NumberPageView,
  NumberReleaseCause,
  NumberRowView,
  OpeningDayView,
  OpeningIntervalView,
  RegionLatencyView,
  RegistrationView,
  SignallingEventView,
  SipConnectionView,
  VoiceObservedView,
  WebChatWidgetView,
  WorkspaceSipCredentialView,
} from "./gateway.js";

/** What the console shows where a SIP password would be. It never holds one to mask. */
export const MASKED_PASSWORD = "••••••••••";

/** What the Connection list says when no registration has been observed at all. */
export const REGISTRATION_NOT_OBSERVED =
  () => (portalText("shell", "not_observed_your_carrier_does_not_report_a_registration_t_4a10404d"));

/** What the region line says when nothing has measured it. */
export const LATENCY_NOT_MEASURED = () => (portalText("shell", "not_measured_yet_5ababade"));

const MEDIUM_STATE: readonly MediumState[] = ["connected", "coming_soon", "not_connected"];

/** Monday-first, matching the configuration document's own `week` keys. */
const WEEKDAY: readonly { readonly key: string; readonly label: string }[] = [
  validatedDisplayView({ key: "monday", get label() { return portalText("shell", "mon_24b2a099"); } }),
  validatedDisplayView({ key: "tuesday", get label() { return portalText("shell", "tue_529541bb"); } }),
  validatedDisplayView({ key: "wednesday", get label() { return portalText("shell", "wed_23408b19"); } }),
  validatedDisplayView({ key: "thursday", get label() { return portalText("shell", "thu_3593ccd9"); } }),
  validatedDisplayView({ key: "friday", get label() { return portalText("shell", "fri_bbd6e32e"); } }),
  validatedDisplayView({ key: "saturday", get label() { return portalText("shell", "sat_6b782d41"); } }),
  validatedDisplayView({ key: "sunday", get label() { return portalText("shell", "sun_48c98cab"); } }),
];

/**
 * How each overflow and after-hours arm reads, in the customer's words.
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-B, OD-4): "take a message" says what happens to it, which is now true for every
 * agent_template — the message is a `taken_message` shown in History and emailed to the workspace's Owners and Admins
 * (the notice, NOTE), where "send you a recap" promised a recap nothing sent.
 */
const BEHAVIOUR_COPY = () => (new Map<string, string>([
  ["closed_message", portalText("shell", "say_you_are_closed_and_give_the_next_opening_time_d5925a4a")],
  ["message_taken", portalText("operations", "channel.recap_message_taken")],
  ["queued", portalText("shell", "hold_the_caller_in_a_queue_until_a_line_frees_up_2d64f3ae")],
  ["transferred", portalText("shell", "transfer_to_the_number_you_nominated_8043b58b")],
]));

export function behaviourCopyOf(kind: string): string {
  return BEHAVIOUR_COPY().get(kind) ?? kind;
}

function readMediumState(value: unknown, path: string): MediumState {
  const raw = readText(value, path);
  const found = MEDIUM_STATE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a medium state", raw);
  return found;
}

function readSipAddressOwner(value: unknown, path: string): "wetalk" | "carrier" {
  const raw = readText(value, path);
  if (raw !== "wetalk" && raw !== "carrier") {
    throw new MalformedResponseError(path, "wetalk or carrier", raw);
  }
  return raw;
}

function readNullableWholeNumber(value: unknown, path: string): number | null {
  if (value === null || value === undefined) return null;
  return readWholeNumber(value, path);
}

function readNullableBoolean(value: unknown, path: string): boolean | null {
  if (value === null || value === undefined) return null;
  return readBoolean(value, path);
}

export function channelBindingOfWire(
  value: unknown,
  path: string,
  now: Date,
): ChannelBindingRowView {
  const one = readRecord(value, path);
  const last = one["last_conversation_at"];
  return validatedDisplayView({
    channelBindingId: readText(one["channel_binding_id"], `${path}.channel_binding_id`),
    kind: readText(one["kind"], `${path}.kind`),
    medium: readMedium(one["medium"], `${path}.medium`),
    address: readText(one["address"], `${path}.address`),
    carrier: readNullableText(one["carrier"] ?? null, `${path}.carrier`),
    enabled: readBoolean(one["enabled"], `${path}.enabled`),
    conversationLast7Day: readWholeNumber(
      one["conversation_last_7_day"],
      `${path}.conversation_last_7_day`,
    ),
    get lastConversationAtText() { return last === null || last === undefined
        ? null
        : formatWhen(readText(last, `${path}.last_conversation_at`), now); },
  });
}

export function concurrencyOfWire(value: unknown, path: string): ConcurrencySummaryView {
  const one = readRecord(value, path);
  const concurrency = readRecord(one["concurrency"], `${path}.concurrency`);
  return {
    inUse: readWholeNumber(one["in_use"], `${path}.in_use`),
    // plan-015 LIM: the EFFECTIVE ceiling — the agent's own limit when set, else the live version's.
    channelUnitCeiling: readNullableWholeNumber(
      one["channel_unit_ceiling"],
      `${path}.channel_unit_ceiling`,
    ),
    voiceAgentState: readText(one["voice_agent_state"], `${path}.voice_agent_state`),
    organizationChannelUnit: readWholeNumber(concurrency["entitlement"], `${path}.concurrency.entitlement`),
    channelUnitLimit: {
      limit: readNullableWholeNumber(one["channel_unit_limit"], `${path}.channel_unit_limit`),
      versionCeiling: readNullableWholeNumber(one["version_ceiling"], `${path}.version_ceiling`),
      entitlementCeiling: readWholeNumber(one["entitlement_ceiling"], `${path}.entitlement_ceiling`),
    },
  };
}

// ── plan-015 LIM: changing a VoiceAgent's lines (G11, ADR 0045) ─────────────────────────────

/**
 * The three figures behind "Calls at once" (`GET /v1/agents/{voice_agent_id}/concurrency`, plan-015 LIM): the
 * agent's own limit, the published version's own ceiling and the organization's entitlement — the most the limit may
 * be. `channelUnitCeiling` beside them is the effective value admission applies.
 */
export interface ChannelUnitLimitView {
  /** `channel_unit_limit` — the agent's own; `null` means the published version's own ceiling. */
  readonly limit: number | null;
  /** The live version's own ceiling; `null` when nothing is published. */
  readonly versionCeiling: number | null;
  /** The organization's concurrent-conversation entitlement across all agents. */
  readonly entitlementCeiling: number;
}

/**
 * `422 channel_unit_limit_above_entitlement`, with the organization's figure the API named in
 * `error.detail.entitlement_ceiling` — so the dialog can say how far it may go and offer "Request more lines".
 */
export class ChannelUnitLimitAboveEntitlementError extends Error {
  override readonly name = "ChannelUnitLimitAboveEntitlementError";
  readonly entitlementCeiling: number;
  constructor(message: string, entitlementCeiling: number) {
    super(message);
    this.entitlementCeiling = entitlementCeiling;
  }
}

/*
 * plan-015 LIM: the method and the read's three figures are merged onto the console's types here. The API binding is
 * {@link channelUnitLimitApiSlice}, spread into `apiGateway` beside the other slices; the sandbox's is in
 * `channel-sandbox.ts`. Required since INT-A (2026-10-09), when the sandbox gateway carried them too.
 */
declare module "./gateway.js" {
  interface ConcurrencySummaryView {
    readonly channelUnitLimit: ChannelUnitLimitView;
  }
  interface ConsoleGateway {
    /**
     * `PUT /v1/agents/{voice_agent_id}/concurrency` — set the agent's own line limit (`null`: back to the published
     * version's own ceiling). Answers the summary after the change; raises {@link ChannelUnitLimitAboveEntitlementError}
     * above the organization's entitlement.
     */
    readonly setVoiceAgentChannelUnitLimit: (voiceAgentId: string, limit: number | null) => Promise<ConcurrencySummaryView>;
  }
}

/** The figure in a `channel_unit_limit_above_entitlement` envelope, or `null` when the answer is anything else. */
function aboveEntitlementOf(result: ApiResult): ChannelUnitLimitAboveEntitlementError | null {
  if (result.status !== 422) return null;
  const error = (result.body as { error?: { code?: unknown; message?: unknown; detail?: { entitlement_ceiling?: unknown } } } | null)?.error;
  if (error?.code !== "channel_unit_limit_above_entitlement") return null;
  const ceiling = error.detail?.entitlement_ceiling;
  if (typeof ceiling !== "number" || !Number.isInteger(ceiling) || ceiling < 0) {
    throw new MalformedResponseError(
      "setVoiceAgentChannelUnitLimit.error.detail.entitlement_ceiling",
      "a whole number from 0",
      ceiling,
    );
  }
  return new ChannelUnitLimitAboveEntitlementError(
    typeof error.message === "string" ? error.message : portalText("shell", "your_organization_allows_value_at_once_across_all_agents_65dcf72d", { v0: String(String(ceiling)) }),
    ceiling,
  );
}

/** The API binding, spread into `apiGateway` (`...channelUnitLimitApiSlice(client)`). */
export function channelUnitLimitApiSlice(client: WeTalkApiClient): {
  readonly setVoiceAgentChannelUnitLimit: (voiceAgentId: string, limit: number | null) => Promise<ConcurrencySummaryView>;
} {
  return {
    async setVoiceAgentChannelUnitLimit(voiceAgentId, limit): Promise<ConcurrencySummaryView> {
      const result = await client.setVoiceAgentChannelUnitLimit({
        path: { voice_agent_id: voiceAgentId },
        body: { channel_unit_limit: limit },
      });
      const refused = aboveEntitlementOf(result);
      if (refused !== null) throw refused;
      const body = readRecord(assertOk(result), "setVoiceAgentChannelUnitLimit");
      return concurrencyOfWire(body["data"], "setVoiceAgentChannelUnitLimit.data");
    },
  };
}

export function sipOfWire(value: unknown, path: string, now: Date): SipConnectionView {
  const one = readRecord(value, path);
  const rotated = one["rotated_at"];
  return validatedDisplayView({
    sipCredentialId: readText(one["sip_credential_id"], `${path}.sip_credential_id`),
    username: readText(one["username"], `${path}.username`),
    connectionHost: readText(one["connection_host"], `${path}.connection_host`),
    sipAddress: readText(one["sip_address"], `${path}.sip_address`),
    sipAddressOwner: readSipAddressOwner(one["sip_address_owner"], `${path}.sip_address_owner`),
    codec: readArray(one["codec"], `${path}.codec`).map((entry, index) =>
      readText(entry, `${path}.codec[${index}]`),
    ),
    secretRef: readText(one["secret_ref"], `${path}.secret_ref`),
    get rotatedAtText() { return rotated === null || rotated === undefined
        ? null
        : formatWhen(readText(rotated, `${path}.rotated_at`), now); },
  });
}

function registrationOfWire(value: unknown, path: string, now: Date): RegistrationView | null {
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  return validatedDisplayView({
    get observedAtText() { return formatWhen(readText(one["observed_at"], `${path}.observed_at`), now); },
    responseCode: readNullableWholeNumber(one["response_code"], `${path}.response_code`),
    synthesised: readBoolean(one["synthesised"], `${path}.synthesised`),
  });
}

function inboundFailoverOfWire(value: unknown, path: string): InboundFailoverView {
  const one = readRecord(value, path);
  return {
    // The wire's `possible` is an enum of exactly `false`; reading it and discarding the
    // value would be pretending there is a case where it is true.
    possible: readBoolean(one["possible"], `${path}.possible`) as false,
    reason: readText(one["reason"], `${path}.reason`),
    explanation: readText(one["explanation"], `${path}.explanation`),
    alternative: readText(one["alternative"], `${path}.alternative`),
  };
}

/** `voice_observed` (review #94). Absent from an API that predates it, which reads as unknown. */
function voiceObservedOfWire(value: unknown, path: string, now: Date): VoiceObservedView | null {
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  const last = one["last_conversation_at"];
  return validatedDisplayView({
    get lastConversationAtText() { return last === null || last === undefined
        ? null
        : formatWhen(readText(last, `${path}.last_conversation_at`), now); },
    conversationLast7Day: readWholeNumber(
      one["conversation_last_7_day"],
      `${path}.conversation_last_7_day`,
    ),
  });
}

export function connectionOfWire(value: unknown, path: string, now: Date): VoiceAgentConnectionView {
  const one = readRecord(value, path);
  const number = one["number"];
  const sip = one["sip"];
  return {
    slug: readText(one["slug"], `${path}.slug`),
    voiceAgentState: readText(one["voice_agent_state"], `${path}.voice_agent_state`),
    number:
      number === null || number === undefined
        ? null
        : (() => {
            const each = readRecord(number, `${path}.number`);
            return {
              e164: readText(each["e164"], `${path}.number.e164`),
              country: readText(each["country"], `${path}.number.country`),
              // `null` for a number on the account's own SIP trunk: no WeTalk carrier homes it.
              carrier: readNullableText(each["carrier"] ?? null, `${path}.number.carrier`),
              source: readText(each["source"], `${path}.number.source`),
              bindingKind: readText(each["binding_kind"], `${path}.number.binding_kind`),
            };
          })(),
    sip: sip === null || sip === undefined ? null : sipOfWire(sip, `${path}.sip`, now),
    eventsUrl: readNullableText(one["events_url"] ?? null, `${path}.events_url`),
    voiceObserved: voiceObservedOfWire(one["voice_observed"], `${path}.voice_observed`, now),
    registration: registrationOfWire(one["registration"], `${path}.registration`, now),
    inboundFailover: inboundFailoverOfWire(one["inbound_failover"], `${path}.inbound_failover`),
  };
}

function intervalOfWire(value: unknown, path: string): OpeningIntervalView {
  const one = readRecord(value, path);
  return {
    opensAt: readText(one["opens_at"], `${path}.opens_at`),
    closesAt: readText(one["closes_at"], `${path}.closes_at`),
  };
}

function intervalListOfWire(value: unknown, path: string): readonly OpeningIntervalView[] {
  return readArray(value, path).map((entry, index) =>
    intervalOfWire(entry, `${path}[${String(index)}]`),
  );
}

function behaviourOfWire(value: unknown, path: string): AnsweringBehaviourView | null {
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  return {
    kind: readText(one["kind"], `${path}.kind`),
    phraseKey: one["phrase_key"] === undefined ? null : readNullableText(one["phrase_key"], `${path}.phrase_key`),
    maxWaitMs: readNullableWholeNumber(one["max_wait_ms"] ?? null, `${path}.max_wait_ms`),
    transferTo: one["transfer_to"] === undefined ? null : readNullableText(one["transfer_to"], `${path}.transfer_to`),
  };
}

export function answeringOfWire(value: unknown, path: string): AnsweringScheduleView {
  const one = readRecord(value, path);
  const hours = one["opening_hours"];
  const hoursRecord =
    hours === null || hours === undefined ? null : readRecord(hours, `${path}.opening_hours`);

  const week: OpeningDayView[] =
    hoursRecord === null
      ? []
      : WEEKDAY.map((day) => {
          const raw = readRecord(hoursRecord["week"], `${path}.opening_hours.week`)[day.key];
          return validatedDisplayView({
            day: day.key,
            get label() { return day.label; },
            // A day the document does not carry is a malformed document, not "closed": the
            // contract requires all seven, and an empty list is how "closed" is spelled.
            interval: intervalListOfWire(raw, `${path}.opening_hours.week.${day.key}`),
          });
        });

  const closedDate: ClosedDateView[] =
    hoursRecord === null
      ? []
      : readArray(hoursRecord["exception"], `${path}.opening_hours.exception`).map(
          (entry, index) => {
            const each = readRecord(entry, `${path}.opening_hours.exception[${index}]`);
            return validatedDisplayView({
              date: readText(each["date"], `${path}.opening_hours.exception[${index}].date`),
              get label() { return readText(each["label"], `${path}.opening_hours.exception[${index}].label`); },
              interval: intervalListOfWire(
                each["interval"],
                `${path}.opening_hours.exception[${index}].interval`,
              ),
            });
          },
        );

  return {
    published: readBoolean(one["published"], `${path}.published`),
    timeZone: readText(one["time_zone"], `${path}.time_zone`),
    configuredTimeZone:
      hoursRecord === null ? null : readText(hoursRecord["time_zone"], `${path}.opening_hours.time_zone`),
    week,
    closedDate,
    afterHours: behaviourOfWire(one["after_hours"], `${path}.after_hours`),
    overflow: behaviourOfWire(one["overflow"], `${path}.overflow`),
    overflowLast7Day: readArray(one["overflow_last_7_day"], `${path}.overflow_last_7_day`).map(
      (entry, index) => {
        const each = readRecord(entry, `${path}.overflow_last_7_day[${index}]`);
        return {
          behaviour: readText(each["behaviour"], `${path}.overflow_last_7_day[${index}].behaviour`),
          occurrence: readWholeNumber(
            each["occurrence"],
            `${path}.overflow_last_7_day[${index}].occurrence`,
          ),
          longestWaitMs: readWholeNumber(
            each["longest_wait_ms"],
            `${path}.overflow_last_7_day[${index}].longest_wait_ms`,
          ),
        };
      },
    ),
  };
}

export function signallingOfWire(value: unknown, path: string, now: Date): SignallingEventView {
  const one = readRecord(value, path);
  return validatedDisplayView({
    signallingEventId: readText(one["signalling_event_id"], `${path}.signalling_event_id`),
    get atText() { return formatWhen(readText(one["occurred_at"], `${path}.occurred_at`), now); },
    kind: readText(one["kind"], `${path}.kind`),
    peer: readNullableText(one["peer"] ?? null, `${path}.peer`),
    responseCode: readNullableWholeNumber(one["response_code"] ?? null, `${path}.response_code`),
    detail: readNullableText(one["detail"] ?? null, `${path}.detail`),
    synthesised: readBoolean(one["synthesised"], `${path}.synthesised`),
  });
}

/** `WorkspacePhoneNumber.provider` (plan-008 ST-AN): a carrier, or the account's own SIP trunk. */
const NUMBER_PROVIDER = ["telnyx", "twilio", "sip_trunk"] as const;

/**
 * The carrier of a listed number, checked against its `provider` (plan-008 ST-AN, hand-off to
 * ST-UN): a trunk number has `carrier: null` and `provider: sip_trunk`; a carrier number names the
 * same carrier twice. Anything else is the table's carrier-XOR-trunk CHECK broken on the wire, so it
 * is refused rather than rendered under a guessed provider.
 */
function numberCarrierOf(one: Readonly<Record<string, unknown>>, path: string): string | null {
  const provider = readText(one["provider"], `${path}.provider`);
  if (!(NUMBER_PROVIDER as readonly string[]).includes(provider)) {
    throw new MalformedResponseError(`${path}.provider`, NUMBER_PROVIDER.join(" | "), provider);
  }
  const carrier = readNullableText(one["carrier"] ?? null, `${path}.carrier`);
  if (provider === "sip_trunk") {
    if (carrier !== null) throw new MalformedResponseError(`${path}.carrier`, "null for a SIP trunk number", carrier);
    return null;
  }
  if (carrier !== provider) throw new MalformedResponseError(`${path}.carrier`, provider, carrier);
  return carrier;
}

/** `2 Jun 2026` — a day, in the browser's zone (numbers-sip-sync: "Since", "In use"). */
export function formatDay(isoTimestamp: string, path: string): string {
  const at = new Date(isoTimestamp);
  if (Number.isNaN(at.getTime())) throw new MalformedResponseError(path, "an RFC 3339 timestamp", isoTimestamp);
  return portalFormat(portalLocale()).date(at, { day: "numeric", month: "short", year: "numeric" });
}

function releaseCauseOf(value: unknown, path: string): NumberReleaseCause | null {
  if (value === null || value === undefined) return null;
  const text = readText(value, path);
  const found = NUMBER_RELEASE_CAUSE.find((one) => one === text);
  if (found === undefined) throw new MalformedResponseError(path, NUMBER_RELEASE_CAUSE.join(" | "), text);
  return found;
}

export function numberRowOfWire(value: unknown, path: string, now: Date): NumberRowView {
  const one = readRecord(value, path);
  const released = one["released_at"];
  const purchasedAt = readText(one["purchased_at"], `${path}.purchased_at`);
  return validatedDisplayView({
    phoneNumberId: readText(one["phone_number_id"], `${path}.phone_number_id`),
    e164: readText(one["e164"], `${path}.e164`),
    country: readText(one["country"], `${path}.country`),
    carrier: numberCarrierOf(one, path),
    source: readText(one["source"], `${path}.source`),
    monthlyCost: money(
      readWholeNumber(one["monthly_cost_minor"], `${path}.monthly_cost_minor`),
      readText(one["currency"], `${path}.currency`) as CurrencyCode,
    ),
    get purchasedAtText() { return formatWhen(readText(one["purchased_at"], `${path}.purchased_at`), now); },
    get releasedAtText() { return released === null || released === undefined
        ? null
        : formatWhen(readText(released, `${path}.released_at`), now); },
    get purchasedOn() { return formatDay(purchasedAt, `${path}.purchased_at`); },
    get releasedOn() { return released === null || released === undefined ? null : formatDay(readText(released, `${path}.released_at`), `${path}.released_at`); },
    // Absent from a server older than numbers-sip-sync: read as "no cause known", never invented.
    releaseCause: releaseCauseOf(one["release_cause"], `${path}.release_cause`),
    sipTrunkId: readNullableText(one["sip_trunk_id"] ?? null, `${path}.sip_trunk_id`),
    sipTrunkDisplayName: readNullableText(one["sip_trunk_display_name"] ?? null, `${path}.sip_trunk_display_name`),
    isCallerId: readBoolean(one["is_caller_id"], `${path}.is_caller_id`),
    channelBindingId: readNullableText(one["channel_binding_id"] ?? null, `${path}.channel_binding_id`),
    bindingKind: readNullableText(one["binding_kind"] ?? null, `${path}.binding_kind`),
    enabled: readNullableBoolean(one["enabled"] ?? null, `${path}.enabled`),
    voiceAgentId: readNullableText(one["voice_agent_id"] ?? null, `${path}.voice_agent_id`),
    voiceAgentName: readNullableText(one["voice_agent_name"] ?? null, `${path}.voice_agent_name`),
    inboundFailover: readBoolean(one["inbound_failover"], `${path}.inbound_failover`),
  });
}

function numberActivityConversationOfWire(value: unknown, path: string, now: Date): NumberActivityConversationView {
  const one = readRecord(value, path);
  const direction = readText(one["direction"], `${path}.direction`);
  if (direction !== "inbound" && direction !== "outbound") {
    throw new MalformedResponseError(`${path}.direction`, "inbound | outbound", direction);
  }
  const billed = one["billed_second"];
  return validatedDisplayView({
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readNullableText(one["voice_agent_name"] ?? null, `${path}.voice_agent_name`),
    direction,
    isTest: readBoolean(one["is_test"], `${path}.is_test`),
    partyAddress: readNullableText(one["party_address"] ?? null, `${path}.party_address`),
    get startedAtText() { return formatWhen(readText(one["started_at"], `${path}.started_at`), now); },
    answered: one["answered_at"] !== null && one["answered_at"] !== undefined,
    billedSecond: billed === null || billed === undefined ? null : readWholeNumber(billed, `${path}.billed_second`),
    outcome: readNullableText(one["outcome"] ?? null, `${path}.outcome`),
    endReason: readText(one["end_reason"], `${path}.end_reason`),
  });
}

/** `getNumberActivity` (numbers-sip-sync). */
export function numberActivityOfWire(body: unknown, now: Date): NumberActivityView {
  const envelope = readRecord(body, "getNumberActivity");
  const data = readRecord(envelope["data"], "getNumberActivity.data");
  const last = data["last_conversation_at"];
  return validatedDisplayView({
    phoneNumberId: readText(data["phone_number_id"], "getNumberActivity.data.phone_number_id"),
    get lastConversationAtText() { return last === null || last === undefined ? null : formatWhen(readText(last, "getNumberActivity.data.last_conversation_at"), now); },
    monthConversationCount: readWholeNumber(data["month_conversation_count"], "getNumberActivity.data.month_conversation_count"),
    conversation: readArray(data["conversation"], "getNumberActivity.data.conversation").map((entry, index) =>
      numberActivityConversationOfWire(entry, `getNumberActivity.data.conversation[${String(index)}]`, now),
    ),
  });
}

export function numberPageOfWire(body: unknown, now: Date): NumberPageView {
  const envelope = readRecord(body, "listNumbers");
  const meta = readRecord(envelope["meta"], "listNumbers.meta");
  return {
    row: readArray(envelope["data"], "listNumbers.data").map((entry, index) =>
      numberRowOfWire(entry, `listNumbers.data[${String(index)}]`, now),
    ),
    sipTrunkCount: readWholeNumber(meta["sip_trunk_count"], "listNumbers.meta.sip_trunk_count"),
  };
}

export function workspaceSipOfWire(
  value: unknown,
  path: string,
  now: Date,
): WorkspaceSipCredentialView {
  const one = readRecord(value, path);
  return extendDisplayView(sipOfWire(value, path, now), {
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
  });
}

export function regionLatencyOfWire(value: unknown, path: string, now: Date): RegionLatencyView {
  const one = readRecord(value, path);
  return {
    measured: readBoolean(one["measured"], `${path}.measured`),
    source: readText(one["source"], `${path}.source`),
    windowHours: readWholeNumber(one["window_hours"], `${path}.window_hours`),
    detail: readText(one["detail"], `${path}.detail`),
    sample: readArray(one["sample"], `${path}.sample`).map((entry, index) => {
      const each = readRecord(entry, `${path}.sample[${index}]`);
      return validatedDisplayView({
        carrier: readText(each["carrier"], `${path}.sample[${index}].carrier`),
        region: readText(each["region"], `${path}.sample[${index}].region`),
        probeKind: readText(each["probe_kind"], `${path}.sample[${index}].probe_kind`),
        rttMs: readWholeNumber(each["rtt_ms"], `${path}.sample[${index}].rtt_ms`),
        get measuredAtText() { return formatWhen(
          readText(each["measured_at"], `${path}.sample[${index}].measured_at`),
          now,
        ); },
      });
    }),
  };
}

export function mediumRowOfWire(
  value: unknown,
  path: string,
  now: Date,
): MessagingMediumRowView {
  const one = readRecord(value, path);
  const last = one["last_conversation_at"];
  const availability = readText(one["availability"], `${path}.availability`);
  if (availability !== "open" && availability !== "waitlisted") {
    throw new MalformedResponseError(`${path}.availability`, "open or waitlisted", availability);
  }
  return validatedDisplayView({
    medium: readMedium(one["medium"], `${path}.medium`),
    get name() { return messagingMediumText(readText(one["name"], `${path}.name`)); },
    get dek() { return messagingMediumText(readText(one["dek"], `${path}.dek`)); },
    get detail() { return messagingMediumText(readText(one["detail"], `${path}.detail`)); },
    availability,
    get waitlistReason() { const value = readNullableText(one["waitlist_reason"], `${path}.waitlist_reason`); return value === null ? null : messagingMediumText(value); },
    state: readMediumState(one["state"], `${path}.state`),
    bindingCount: readWholeNumber(one["binding_count"], `${path}.binding_count`),
    enabledBindingCount: readWholeNumber(
      one["enabled_binding_count"],
      `${path}.enabled_binding_count`,
    ),
    conversationLast30Day: readWholeNumber(
      one["conversation_last_30_day"],
      `${path}.conversation_last_30_day`,
    ),
    liveConversationCount: readWholeNumber(
      one["live_conversation_count"],
      `${path}.live_conversation_count`,
    ),
    get lastConversationAtText() { return last === null || last === undefined
        ? null
        : formatWhen(readText(last, `${path}.last_conversation_at`), now); },
  });
}

/**
 * The widget, with its tag composed against the console's own API origin.
 *
 * `apiOrigin` is the configured base URL, validated at import with no default. The API hands
 * over a path rather than a URL precisely so that no unprovisioned hostname reaches a
 * customer's page source.
 */
export function widgetOfWire(
  value: unknown,
  path: string,
  apiOrigin: string,
): WebChatWidgetView | null {
  if (value === null || value === undefined) return null;
  // AMENDED 2026-09-26 (plan-004 WC-80, design §17.10.5, reviews #175/#176): the detail's `widget` is
  // now the LIST of website widgets (`WebChatWidget[]`), each carrying its full `snippet` composed by
  // the API against the runtime's `rtc` host — the tag composed here against the console's own
  // origin loaded from a host that serves no widget (404). Until the Web chat page shows every
  // widget (WC-81), the one shown is the first that is answering, else the first. An API older than
  // WC-80 still sends one `{ widget_key, script_path }`, which is decoded as before.
  if (Array.isArray(value)) {
    const decoded = value.map((entry, index) => oneWidgetOfWire(entry, `${path}[${String(index)}]`, apiOrigin));
    const answering = decoded.find((one) => one.answering);
    const shown = answering === undefined ? decoded[0] : answering;
    return shown === undefined ? null : { widgetKey: shown.widgetKey, scriptTag: shown.scriptTag };
  }
  const one = oneWidgetOfWire(value, path, apiOrigin);
  return { widgetKey: one.widgetKey, scriptTag: one.scriptTag };
}

function oneWidgetOfWire(
  value: unknown,
  path: string,
  apiOrigin: string,
): WebChatWidgetView & { readonly answering: boolean } {
  const one = readRecord(value, path);
  const widgetKey = readText(one["widget_key"], `${path}.widget_key`);
  if (one["snippet"] !== undefined) {
    return {
      widgetKey,
      scriptTag: readText(one["snippet"], `${path}.snippet`),
      answering: readText(one["state"], `${path}.state`) === "answering",
    };
  }
  const scriptPath = readText(one["script_path"], `${path}.script_path`);
  return { widgetKey, scriptTag: widgetTagOf(apiOrigin, scriptPath, widgetKey), answering: true };
}

/** The one place the tag's text is written. */
export function widgetTagOf(apiOrigin: string, scriptPath: string, widgetKey: string): string {
  const source = new URL(scriptPath, apiOrigin).toString();
  return `<script src="${source}" data-wetalk-widget="${widgetKey}" async></script>`;
}

export function mediumDetailOfWire(
  value: unknown,
  path: string,
  now: Date,
  apiOrigin: string,
): MessagingMediumDetailView {
  const one = readRecord(value, path);
  return extendDisplayView(mediumRowOfWire(value, path, now), {
    get blurb() { return messagingMediumText(readText(one["blurb"], `${path}.blurb`)); },
    binding: readArray(one["binding"], `${path}.binding`).map((entry, index) => {
      const each = readRecord(entry, `${path}.binding[${index}]`);
      return {
        channelBindingId: readText(
          each["channel_binding_id"],
          `${path}.binding[${index}].channel_binding_id`,
        ),
        kind: readText(each["kind"], `${path}.binding[${index}].kind`),
        address: readText(each["address"], `${path}.binding[${index}].address`),
        enabled: readBoolean(each["enabled"], `${path}.binding[${index}].enabled`),
        carrier: readNullableText(
          each["carrier"] ?? null,
          `${path}.binding[${index}].carrier`,
        ),
        voiceAgentId: readText(
          each["voice_agent_id"],
          `${path}.binding[${index}].voice_agent_id`,
        ),
        voiceAgentName: readText(
          each["voice_agent_name"],
          `${path}.binding[${index}].voice_agent_name`,
        ),
      };
    }),
    billedSecondLast30Day: readWholeNumber(
      one["billed_second_last_30_day"],
      `${path}.billed_second_last_30_day`,
    ),
    step: readArray(one["step"], `${path}.step`).map((entry, index) => {
      const each = readRecord(entry, `${path}.step[${index}]`);
      return validatedDisplayView({
        get title() { return messagingMediumText(readText(each["title"], `${path}.step[${index}].title`)); },
        get dek() { return messagingMediumText(readText(each["dek"], `${path}.step[${index}].dek`)); },
        get actionLabel() { const value = readNullableText(
          each["action_label"],
          `${path}.step[${index}].action_label`,
        ); return value === null ? null : messagingMediumText(value); },
      });
    }),
    field: readArray(one["field"], `${path}.field`).map((entry, index) => {
      const each = readRecord(entry, `${path}.field[${index}]`);
      return validatedDisplayView({
        code: readText(each["code"], `${path}.field[${index}].code`),
        get label() { return messagingMediumText(readText(each["label"], `${path}.field[${index}].label`)); },
        get placeholder() { return messagingMediumText(readText(each["placeholder"], `${path}.field[${index}].placeholder`)); },
      });
    }),
    answerableVoiceAgent: readArray(
      one["answerable_voice_agent"],
      `${path}.answerable_voice_agent`,
    ).map((entry, index) => {
      const each = readRecord(entry, `${path}.answerable_voice_agent[${index}]`);
      return {
        voiceAgentId: readText(
          each["voice_agent_id"],
          `${path}.answerable_voice_agent[${index}].voice_agent_id`,
        ),
        name: readText(each["name"], `${path}.answerable_voice_agent[${index}].name`),
      };
    }),
    widget: widgetOfWire(one["widget"], `${path}.widget`, apiOrigin),
  });
}
